import { getSettings } from "../db";

export type ChatMessage = {
  role: "system" | "user" | "assistant";
  content: string;
};

export function chatCompletionsUrl(base: string): string {
  const trimmed = base.replace(/\/+$/, "");
  if (trimmed.endsWith("/v1/chat/completions")) {
    return trimmed;
  }
  if (trimmed.endsWith("/v1")) {
    return `${trimmed}/chat/completions`;
  }
  return `${trimmed}/v1/chat/completions`;
}

export function chatErrorMessage(status: number, bodyText: string): string {
  if (status === 401) {
    return "API Key 无效或已过期，请在设置页检查密钥。";
  }
  if (status === 429) {
    return "请求过于频繁，请稍后再试。";
  }
  const detail = bodyText.trim().slice(0, 200);
  return detail ? `AI 请求失败 (${status})：${detail}` : `AI 请求失败 (${status})`;
}

export async function streamChat(
  messages: ChatMessage[],
  options: {
    onChunk?: (aggregated: string, delta: string) => void;
    signal?: AbortSignal;
    temperature?: number;
  } = {},
): Promise<string> {
  const settings = await getSettings();
  const url = chatCompletionsUrl(settings.ollamaApiUrl);
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (settings.apiKey) {
    headers.Authorization = `Bearer ${settings.apiKey}`;
  }
  const res = await fetch(url, {
    method: "POST",
    headers,
    signal: options.signal,
    body: JSON.stringify({
      model: settings.ollamaModel,
      messages,
      stream: true,
      temperature: options.temperature ?? 0.4,
    }),
  });

  if (!res.ok) {
    const bodyText = await res.text().catch(() => "");
    throw new Error(chatErrorMessage(res.status, bodyText));
  }
  if (!res.body) {
    throw new Error("AI 响应为空");
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let aggregated = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) {
      break;
    }
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop() ?? "";

    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed.startsWith("data:")) {
        continue;
      }
      const payload = trimmed.slice(5).trim();
      if (!payload || payload === "[DONE]") {
        continue;
      }
      try {
        const json = JSON.parse(payload) as {
          choices?: Array<{ delta?: { content?: string } }>;
        };
        const delta = json.choices?.[0]?.delta?.content ?? "";
        if (!delta) {
          continue;
        }
        aggregated += delta;
        options.onChunk?.(aggregated, delta);
      } catch {
        // ignore malformed chunks
      }
    }
  }

  return aggregated.trim();
}

export {
  buildDiagnosisPrompt,
  buildSelectionQuestionPrompt,
  buildStepSimplifyPrompt,
} from "./prompts";
